import dayjs from 'dayjs';
import { TFunction } from 'i18next';

import {
	Booking as IDriverBooking,
	BookingStateEnum,
	IWhoami,
	LegView,
	PassengerBookingView as IPassengerBooking,
	PassengerBookingView,
	PassengerDetailView,
	PassengerLegStatusEnum,
} from '@mopla/data-models';

import {
	checkHasLegStatus,
	getItineraryLegsTimeInfoAdapters,
	isSpecialSchoolTrafficItinerary,
} from './itinerary';

export const calculateBookingExtraPassengers = (booking: IDriverBooking) => {
	return (
		booking.passengerList.filter((b) => !b.isMainPassenger).length +
		(booking.toddlerSeats || 0)
	);
};

export const getMainPassenger = (booking: IDriverBooking) => {
	return booking.passengerList.find((b) => b.isMainPassenger);
};

export const checkIfPriceAccordingTarif = (booking: IDriverBooking) => {
	/** This exact condition is discussed and validated by the BE team */
	return booking.paymentForm === 'CASH' && booking.paymentAmount === null;
};

/** Who cancelled a ride — the `CANCELLED_BY_*` booking statuses, named by role. */
export enum ERideCanceller {
	PASSENGER = 'PASSENGER',
	DRIVER = 'DRIVER',
	OPERATOR = 'OPERATOR',
}

const CANCELLER_BY_BOOKING_STATUS: Partial<
	Record<BookingStateEnum, ERideCanceller>
> = {
	[BookingStateEnum.CANCELLED_BY_PASSENGER]: ERideCanceller.PASSENGER,
	[BookingStateEnum.CANCELLED_BY_DRIVER]: ERideCanceller.DRIVER,
	[BookingStateEnum.CANCELLED_BY_DISTRIBUTOR]: ERideCanceller.OPERATOR,
};

/**
 * Resolves who cancelled the ride, or null when it is not cancelled — so
 * consumers never have to read the raw booking status to tell both apart.
 */
export const getRideCanceller = (
	booking: IPassengerBooking | Partial<IPassengerBooking>
): ERideCanceller | null => {
	if (!booking.bookingStatus) return null;

	return CANCELLER_BY_BOOKING_STATUS[booking.bookingStatus] ?? null;
};

export const checkIsBookingCancelled = (
	booking: IPassengerBooking | Partial<IPassengerBooking>
) => getRideCanceller(booking) !== null;

export enum ERideStatus {
	ARRIVED = 'ARRIVED',
	BOOKED = 'BOOKED',
	CANCELLED = 'CANCELLED',
	CHECKED_IN = 'CHECKED_IN',
	COMPLETED = 'COMPLETED',
	/** legStatus ON_THE_WAY while the booking is EXECUTING: the passenger is in the vehicle */
	IN_RIDE = 'IN_RIDE',
	/** legStatus ON_THE_WAY while the booking is NEW: the driver is heading to the pickup */
	ON_THE_WAY = 'ON_THE_WAY',
	SCHEDULED = 'SCHEDULED',
}

/** The leg standing for the ride: only legs that carry a status count, and finished
 *  or cancelled ones are skipped so a multi-leg ride reports the leg still to come. */
export const getRideStatusLeg = (
	itinerary: IPassengerBooking['itinerary']
): LegView | undefined => {
	const statusLegs = itinerary.legs.filter(checkHasLegStatus);

	return (
		statusLegs.find(
			(leg) =>
				leg.legStatus !== PassengerLegStatusEnum.COMPLETED &&
				leg.legStatus !== PassengerLegStatusEnum.CANCELLED
		) ?? statusLegs[statusLegs.length - 1]
	);
};

/**
 * The ride status is a composite of the booking status and the status-bearing
 * leg's status: the same legStatus ON_THE_WAY means "driver is heading to the
 * pickup" while the booking is NEW, but "passenger is in the vehicle" once the
 * booking is EXECUTING. Booking status wins for cancelled and finished rides.
 */
export const getRideStatus = (
	booking: IPassengerBooking
): ERideStatus | null => {
	const { bookingStatus, itinerary } = booking;

	if (checkIsBookingCancelled(booking)) return ERideStatus.CANCELLED;
	if (bookingStatus === BookingStateEnum.FINISHED) return ERideStatus.COMPLETED;

	const statusLeg = getRideStatusLeg(itinerary);

	switch (statusLeg?.legStatus) {
		case PassengerLegStatusEnum.CANCELLED:
			return ERideStatus.CANCELLED;
		case PassengerLegStatusEnum.COMPLETED:
			return ERideStatus.COMPLETED;
		case PassengerLegStatusEnum.CHECKED_IN:
			return ERideStatus.CHECKED_IN;
		case PassengerLegStatusEnum.ON_THE_WAY:
			return bookingStatus === BookingStateEnum.EXECUTING
				? ERideStatus.IN_RIDE
				: ERideStatus.ON_THE_WAY;
		case PassengerLegStatusEnum.DRIVER_ARRIVED:
			return ERideStatus.ARRIVED;
		case PassengerLegStatusEnum.SCHEDULED:
			return ERideStatus.SCHEDULED;
		case PassengerLegStatusEnum.BOOKED:
			return ERideStatus.BOOKED;
		default:
			return null;
	}
};

/** TODO consider this logic again
 * Which conditions should be met for *passed* and *upcoming* bookings?
 * Those implementations originate from the TicketsList.tsx
 * */
export const checkIsPassedBooking = (booking?: IPassengerBooking) => {
	if (!booking || checkIsBookingCancelled(booking)) return false;

	const { endTimeInfoAdapter } = getItineraryLegsTimeInfoAdapters(
		booking.itinerary.legs
	);

	const diffInHours = dayjs().diff(
		dayjs(endTimeInfoAdapter.effectiveEndTime),
		'hours'
	);

	return booking.bookingStatus !== BookingStateEnum.NEW || diffInHours > 24;
};

export const checkIsUpcomingBooking = (booking: IPassengerBooking) => {
	if (checkIsBookingCancelled(booking)) return false;

	const { endTimeInfoAdapter } = getItineraryLegsTimeInfoAdapters(
		booking.itinerary.legs
	);

	const diffInHours = dayjs().diff(
		dayjs(endTimeInfoAdapter.effectiveEndTime),
		'hours'
	);

	return booking.bookingStatus === BookingStateEnum.NEW && diffInHours <= 24;
};

/** True when the in-app cancellation deadline has passed, based on the
 *  BE-provided `cancellableUntil`. If the field is absent we don't block
 *  (let the BE reject if it's actually too late). */
export const checkIsSchoolCancellationDeadlinePassed = (
	booking: IPassengerBooking
) => {
	if (!booking.cancellableUntil) return false;

	return dayjs().isAfter(dayjs(booking.cancellableUntil));
};

/** True when an active school (RMBLS) ride is past its in-app cancellation
 *  deadline (`cancellableUntil`) and can no longer be cancelled in-app. */
export const checkIsSchoolCancellationBlocked = (booking: IPassengerBooking) =>
	isSpecialSchoolTrafficItinerary(booking.itinerary) &&
	!checkIsBookingCancelled(booking) &&
	!checkIsPassedBooking(booking) &&
	checkIsSchoolCancellationDeadlinePassed(booking);

export const checkIsBookingForCurrentUser = (
	booking: IPassengerBooking | Partial<IPassengerBooking>,
	user: IWhoami
) => {
	/** The main passenger should always exists */
	const mainPassenger = booking.passengerList?.find((p) => p.isMainPassenger);

	if (!mainPassenger) {
		return false;
	}

	return (
		JSON.stringify([
			mainPassenger.email,
			mainPassenger.firstName,
			mainPassenger.lastName,
		]) === JSON.stringify([user.email, user.firstName, user.lastName])
	);
};

export const getBookingAttendeesSummary = (
	booking: IPassengerBooking | Partial<IPassengerBooking>,
	currentUser: IWhoami | null,
	t: TFunction
) => {
	if (!currentUser || !booking.passengerList) {
		return null;
	}

	const mainPassenger = booking.passengerList.find(
		(p) => p.isMainPassenger
	) as PassengerDetailView;

	const isTicketForCurrentUser = checkIsBookingForCurrentUser(
		booking,
		currentUser
	);

	const extraPassengersCount = booking.passengerList.length - 1;

	const mainPassengerName = `${mainPassenger.firstName} ${mainPassenger.lastName}`;

	return {
		mainPassengerName,
		title: isTicketForCurrentUser
			? t('tickets:title.your_ticket')
			: mainPassengerName,
		extraPassengers: extraPassengersCount
			? t('tickets:text.adults', { count: extraPassengersCount })
			: null,
		extraChildren: booking.toddlerSeats
			? t('tickets:text.children', {
					count: booking.toddlerSeats,
			  })
			: null,
		luggage: booking.bulkyLuggage
			? t('tickets:text.luggage', {
					count: 1,
			  })
			: null,
	};
};

export const sortBookingsByStartDate = (
	booking1: IPassengerBooking,
	booking2: IPassengerBooking
): number => {
	const { startTimeInfoAdapter: booking1LegTimingAdapter } =
		getItineraryLegsTimeInfoAdapters(booking1.itinerary.legs);
	const { startTimeInfoAdapter: booking2LegTimingAdapter } =
		getItineraryLegsTimeInfoAdapters(booking2.itinerary.legs);

	return (
		booking1LegTimingAdapter.effectiveStartTime.getTime() -
		booking2LegTimingAdapter.effectiveStartTime.getTime()
	);
};

export const getSoonTrips = (bookings: PassengerBookingView[] | null) => {
	return (bookings || []).filter((booking) => {
		const { endTimeInfoAdapter } = getItineraryLegsTimeInfoAdapters(
			booking.itinerary.legs
		);

		/** Positive number in case effectiveEndTime is after {now} */
		const timeDifference = dayjs(endTimeInfoAdapter.effectiveEndTime).diff(
			dayjs(),
			'hour'
		);

		return booking.bookingStatus === 'NEW' && timeDifference <= 1;
	});
};
